import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { subscribeUserToPush, unsubscribeUserFromPush } from "../pushNotifications";
import { profileAPI, techniquesAPI, citiesAPI, workSettingsAPI } from "../api";

// ========== Модальное окно ==========
const Modal = ({ visible, onClose, children }) => {
    if (!visible) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" onClick={onClose}>
            <div className="bg-white rounded-t-3xl w-full max-h-[80vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-center mb-4">
                    <div className="w-12 h-1 bg-gray-300 rounded-full" />
                </div>
                {children}
            </div>
        </div>
    );
};

// ========== Основной экран ==========
export default function SettingsScreen() {
    const { user, signOut } = useAuth();

    // ---------- Аватар ----------
    const [avatar, setAvatar] = useState(null);
    useEffect(() => {
        const saved = localStorage.getItem("@user_avatar");
        if (saved) setAvatar(saved);
    }, []);

    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = reader.result;
            localStorage.setItem("@user_avatar", dataUrl);
            setAvatar(dataUrl);
        };
        reader.readAsDataURL(file);
    };

    // ---------- Смена пароля ----------
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordLoading, setPasswordLoading] = useState(false);

    const handleChangePassword = async () => {
        if (!oldPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
            alert("Заполните все поля");
            return;
        }
        if (newPassword !== confirmPassword) {
            alert("Пароли не совпадают");
            return;
        }
        if (newPassword.length < 6) {
            alert("Минимум 6 символов");
            return;
        }
        setPasswordLoading(true);
        try {
            await profileAPI.changePassword(user.uid_master, oldPassword, newPassword);
            alert("Пароль изменён");
            setOldPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch (err) {
            alert(err.response?.data?.message || "Ошибка");
        } finally {
            setPasswordLoading(false);
        }
    };

    // ---------- Рабочие настройки ----------
    const [techniques, setTechniques] = useState([]);
    const [selectedTechniques, setSelectedTechniques] = useState([]);
    const [cities, setCities] = useState([]);
    const [selectedCities, setSelectedCities] = useState([]);
    const [isOnVacation, setIsOnVacation] = useState(false);
    const [returnDate, setReturnDate] = useState(new Date().toISOString().split("T")[0]);

    // Модальные окна
    const [techModalVisible, setTechModalVisible] = useState(false);
    const [cityModalVisible, setCityModalVisible] = useState(false);
    const [techSearch, setTechSearch] = useState("");
    const [citySearch, setCitySearch] = useState("");
    const [tempSelectedTech, setTempSelectedTech] = useState([]);
    const [tempSelectedCities, setTempSelectedCities] = useState([]);

    // Рефы для восстановления скролла
    const cityListRef = useRef(null);
    const scrollPositionRef = useRef(0);

    // Сохраняем позицию скролла перед обновлением и восстанавливаем после
    useLayoutEffect(() => {
        if (cityListRef.current && scrollPositionRef.current > 0) {
            cityListRef.current.scrollTop = scrollPositionRef.current;
        }
    }, [tempSelectedCities]); // Зависимость от tempSelectedCities, чтобы восстанавливать после каждого клика

    const loadTechniques = async () => {
        try {
            const res = await techniquesAPI.getTechniques();
            const data = res.data?.kn_lib_technic || res.data?.data || (Array.isArray(res.data) ? res.data : []);
            setTechniques(data.map((t) => ({ id: t.technic_e, name: t.name_technic })));
        } catch (e) {
            console.warn("Ошибка загрузки техники");
        }
    };

    // Загрузка городов с удалением дубликатов
    const loadCities = async () => {
        try {
            const res = await citiesAPI.getCities();
            const data = res.data?.kn_lib_city || [];
            const uniqueCities = [];
            const seen = new Set();
            for (const c of data) {
                const key = `${c.name_city}/${c.region_dop_city || ""}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    uniqueCities.push({ name: c.name_city, region: c.region_dop_city || "" });
                }
            }
            setCities(uniqueCities);
        } catch (e) {
            console.warn("Ошибка загрузки городов");
        }
    };

    const loadCurrentSettings = async () => {
        try {
            const res = await workSettingsAPI.getWorkSettings({ userId: user.uid_master });
            if (res.data) {
                setSelectedTechniques(res.data.techniques || []);
                setSelectedCities(res.data.cities || []);
                setIsOnVacation(res.data.vacation || false);
                if (res.data.returnDate) setReturnDate(res.data.returnDate.split("T")[0]);
            }
        } catch (e) {
            console.warn("Нет сохранённых настроек");
        }
    };

    useEffect(() => {
        if (user?.uid_master) {
            loadTechniques();
            loadCities();
            loadCurrentSettings();
        }
    }, [user?.uid_master]);

    // Обработчики техники
    const openTechModal = () => {
        setTempSelectedTech([...selectedTechniques]);
        setTechSearch("");
        setTechModalVisible(true);
    };
    const toggleTech = (id) => {
        setTempSelectedTech((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };
    const confirmTech = () => {
        setSelectedTechniques(tempSelectedTech);
        setTechModalVisible(false);
    };

    // Обработчики городов
    const openCityModal = () => {
        setTempSelectedCities([...selectedCities]);
        setCitySearch("");
        setCityModalVisible(true);
    };

    // Модифицированный toggleCity с сохранением позиции скролла
    const toggleCity = (cityKey) => {
        // Сохраняем текущую позицию скролла перед изменением состояния
        if (cityListRef.current) {
            scrollPositionRef.current = cityListRef.current.scrollTop;
        }
        setTempSelectedCities((prev) => (prev.includes(cityKey) ? prev.filter((c) => c !== cityKey) : [...prev, cityKey]));
    };

    const confirmCities = () => {
        setSelectedCities(tempSelectedCities);
        setCityModalVisible(false);
    };

    // Фильтрация и группировка техники
    const filteredTech = useMemo(() => {
        if (!techSearch) return techniques;
        return techniques.filter((t) => t.name.toLowerCase().includes(techSearch.toLowerCase()));
    }, [techniques, techSearch]);

    // Группировка городов с приоритетной сортировкой регионов
    const groupedCities = useMemo(() => {
        const filtered = cities.filter((c) => c.name.toLowerCase().includes(citySearch.toLowerCase()));

        const groups = {};
        filtered.forEach((c) => {
            const region = c.region || "Другие";
            if (!groups[region]) groups[region] = [];
            groups[region].push(c);
        });

        // Сортировка городов внутри группы: выбранные вверх
        Object.keys(groups).forEach((region) => {
            groups[region].sort((a, b) => {
                const aKey = `${a.name}/${a.region}`;
                const bKey = `${b.name}/${b.region}`;
                const aSelected = tempSelectedCities.includes(aKey);
                const bSelected = tempSelectedCities.includes(bKey);
                if (aSelected && !bSelected) return -1;
                if (!aSelected && bSelected) return 1;
                return a.name.localeCompare(b.name);
            });
        });

        let sections = Object.entries(groups).map(([title, data]) => ({ title, data }));

        // Приоритет регионов: Московская область, МСК, остальные по алфавиту
        const priority = (region) => {
            if (region === "Московская область") return 0;
            if (region === "МСК") return 1;
            return 2;
        };
        sections.sort((a, b) => {
            const pA = priority(a.title);
            const pB = priority(b.title);
            if (pA !== pB) return pA - pB;
            return a.title.localeCompare(b.title);
        });

        return sections;
    }, [cities, citySearch, tempSelectedCities]);

    // Сохранение настроек
    const handleSaveSettings = async () => {
        try {
            await workSettingsAPI.updateWorkSettings({
                userId: user.uid_master,
                techniques: selectedTechniques,
                cities: selectedCities,
                vacation: isOnVacation,
                returnDate: isOnVacation ? new Date(returnDate).toISOString() : null,
            });
            alert("Настройки сохранены");
        } catch (e) {
            alert("Ошибка сохранения");
        }
    };

    // ---------- Push-уведомления ----------
    const [isSubscribed, setIsSubscribed] = useState(false);
    useEffect(() => {
        if ("serviceWorker" in navigator && "PushManager" in window) {
            navigator.serviceWorker.ready.then((reg) => reg.pushManager.getSubscription().then((sub) => setIsSubscribed(!!sub)));
        }
    }, []);

    const togglePush = async () => {
        if (!user) return;
        if (!isSubscribed) {
            const sub = await subscribeUserToPush(user.uid_master);
            if (sub) setIsSubscribed(true);
        } else {
            await unsubscribeUserFromPush();
            setIsSubscribed(false);
        }
    };

    // ========== Рендер ==========
    return (
        <div className="px-4 pt-4 pb-20">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Настройки</h2>

            {/* Аватар */}
            <div className="bg-white p-6 rounded-xl shadow-sm mb-4 flex flex-col items-center">
                <div className="w-32 h-32 bg-blue-100 rounded-full flex items-center justify-center mb-3 overflow-hidden">
                    {avatar ? <img src={avatar} alt="avatar" className="w-full h-full object-cover" /> : <span className="text-4xl">📷</span>}
                </div>
                <label className="bg-blue-500 text-white py-2 px-4 rounded-full cursor-pointer">
                    {avatar ? "Изменить фото" : "Загрузить фото"}
                    <input type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
                </label>
            </div>

            {/* Смена пароля */}
            <div className="bg-white p-6 rounded-xl shadow-sm mb-4">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Смена пароля</h3>
                <div className="mb-3">
                    <label className="text-gray-700 mb-1 block">Старый пароль</label>
                    <input
                        type="password"
                        className="w-full border border-gray-300 rounded-lg px-4 py-3"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        placeholder="Введите старый пароль"
                    />
                </div>
                <div className="mb-3">
                    <label className="text-gray-700 mb-1 block">Новый пароль</label>
                    <input
                        type="password"
                        className="w-full border border-gray-300 rounded-lg px-4 py-3"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Введите новый пароль"
                    />
                </div>
                <div className="mb-4">
                    <label className="text-gray-700 mb-1 block">Подтверждение</label>
                    <input
                        type="password"
                        className="w-full border border-gray-300 rounded-lg px-4 py-3"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Повторите новый пароль"
                    />
                </div>
                <button onClick={handleChangePassword} disabled={passwordLoading} className="w-full bg-blue-500 text-white py-3 rounded-lg font-semibold disabled:opacity-50">
                    {passwordLoading ? "Сохранение..." : "Изменить пароль"}
                </button>
            </div>

            {/* Рабочие настройки: техника */}
            <div className="bg-white p-6 rounded-xl shadow-sm mb-4">
                <label className="text-gray-700 font-medium mb-2 block">Работает с техникой</label>
                <div onClick={openTechModal} className="border border-gray-300 rounded-lg px-4 py-3 flex justify-between items-center cursor-pointer">
                    <span className={selectedTechniques.length ? "text-gray-800" : "text-gray-400"}>
                        {selectedTechniques.length ? selectedTechniques.map((id) => techniques.find((t) => t.id === id)?.name || id).join(", ") : "Выберите технику"}
                    </span>
                    <span className="text-gray-400">▼</span>
                </div>
            </div>

            {/* Рабочие настройки: города */}
            <div className="bg-white p-6 rounded-xl shadow-sm mb-4">
                <label className="text-gray-700 font-medium mb-2 block">Работает в городах</label>
                <div onClick={openCityModal} className="border border-gray-300 rounded-lg px-4 py-3 flex justify-between items-center cursor-pointer">
                    <span className={selectedCities.length ? "text-gray-800" : "text-gray-400"}>
                        {selectedCities.length ? selectedCities.map((key) => key.split("/")[0]).join(", ") : "Выберите города"}
                    </span>
                    <span className="text-gray-400">▼</span>
                </div>
            </div>

            {/* Отпуск */}
            <div className="bg-white p-6 rounded-xl shadow-sm mb-4">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-700 font-medium">В отпуске</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" checked={isOnVacation} onChange={(e) => setIsOnVacation(e.target.checked)} />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-blue-500 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                    </label>
                </div>
                {isOnVacation && (
                    <div>
                        <label className="text-gray-700 mb-1 block">Дата выхода</label>
                        <input
                            type="date"
                            className="border border-gray-300 rounded-lg px-4 py-3 w-full"
                            value={returnDate}
                            onChange={(e) => setReturnDate(e.target.value)}
                            min={new Date().toISOString().split("T")[0]}
                        />
                    </div>
                )}
            </div>

            <button onClick={handleSaveSettings} className="w-full bg-blue-500 text-white py-3 rounded-xl font-bold text-lg mb-4">
                Сохранить настройки
            </button>

            {/* Push */}
            <div className="bg-white p-6 rounded-xl shadow-sm mb-4">
                <h3 className="font-semibold mb-2">Уведомления</h3>
                <button onClick={togglePush} className="bg-blue-500 text-white py-2 px-4 rounded-full">
                    {isSubscribed ? "Отключить push" : "Включить push"}
                </button>
            </div>

            <button onClick={signOut} className="w-full bg-red-500 text-white py-3 rounded-xl font-bold text-lg">
                Выйти
            </button>

            {/* Модалка техники */}
            <Modal visible={techModalVisible} onClose={() => setTechModalVisible(false)}>
                <h3 className="text-xl font-bold mb-3">Выберите технику</h3>
                <input type="text" placeholder="Поиск..." className="w-full border rounded-lg px-3 py-2 mb-3" value={techSearch} onChange={(e) => setTechSearch(e.target.value)} />
                <div className="max-h-60 overflow-y-auto mb-4">
                    {filteredTech.map((t) => (
                        <div key={t.id} onClick={() => toggleTech(t.id)} className="flex justify-between items-center py-2 border-b border-gray-100 cursor-pointer">
                            <span>{t.name}</span>
                            {tempSelectedTech.includes(t.id) && <span className="text-blue-500">✓</span>}
                        </div>
                    ))}
                </div>
                <div className="flex space-x-2">
                    <button onClick={() => setTechModalVisible(false)} className="flex-1 bg-gray-200 py-3 rounded-lg">
                        Отмена
                    </button>
                    <button onClick={confirmTech} className="flex-1 bg-blue-500 text-white py-3 rounded-lg">
                        Выбрать
                    </button>
                </div>
            </Modal>

            {/* Модалка городов */}
            <Modal visible={cityModalVisible} onClose={() => setCityModalVisible(false)}>
                <h3 className="text-xl font-bold mb-3">Выберите города</h3>
                <input type="text" placeholder="Поиск..." className="w-full border rounded-lg px-3 py-2 mb-3" value={citySearch} onChange={(e) => setCitySearch(e.target.value)} />
                <div className="max-h-60 overflow-y-auto mb-4" ref={cityListRef}>
                    {groupedCities.map((group) => (
                        <div key={group.title}>
                            <div className="bg-gray-100 px-3 py-1 font-semibold text-gray-600">{group.title}</div>
                            {group.data.map((city) => {
                                const key = `${city.name}/${city.region}`;
                                return (
                                    <div key={key} onClick={() => toggleCity(key)} className="flex justify-between items-center py-2 border-b border-gray-100 cursor-pointer pl-3">
                                        <span>{city.name}</span>
                                        {tempSelectedCities.includes(key) && <span className="text-blue-500">✓</span>}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
                <div className="flex space-x-2">
                    <button onClick={() => setCityModalVisible(false)} className="flex-1 bg-gray-200 py-3 rounded-lg">
                        Отмена
                    </button>
                    <button onClick={confirmCities} className="flex-1 bg-blue-500 text-white py-3 rounded-lg">
                        Выбрать
                    </button>
                </div>
            </Modal>
        </div>
    );
}
